import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Label2, StatusBadge } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Gauge, LinkSimple, WarningCircle, TreeStructure, ArrowClockwise, GitBranch, FileText,
  ShieldWarning, CheckCircle, MagicWand, Question,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const KLASSE_TONE = { geringfuegig: "text-success", erheblich: "text-warning", gefaehrlich: "text-danger" };
const DEFECT_STATUS_TONE = { offen: "amber", beauftragt: "blue", behoben: "green", abgelehnt: "red" };
const NEXT_STATUS = { offen: ["beauftragt", "abgelehnt"], beauftragt: ["behoben", "abgelehnt"], behoben: [], abgelehnt: ["offen"] };

// ---------------------------------------------------------------- P1: Zuordnung
function MappingTab() {
  const [data, setData] = useState(null);
  const load = () => api.get("/v1/twin/component-mapping").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const setMap = async (kind, key) => {
    try {
      await api.put(`/v1/twin/component-mapping/${kind}`, { cms_komponente_key: key || null });
      toast.success("Zuordnung gespeichert");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const autoApply = async () => {
    try {
      const r = await api.post("/v1/twin/component-mapping/auto-apply", {});
      toast.success(`${r.data.count} eindeutige Zuordnung(en) übernommen`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (data === null) return <Skeleton className="h-64 rounded-md" />;
  if (data === false) return <p className="text-sm text-muted-foreground">Zuordnung konnte nicht geladen werden.</p>;

  const offen = data.rows.filter((r) => r.status === "offen").length;
  const vorschlag = data.rows.filter((r) => r.status === "vorschlag").length;
  const bestaetigt = data.rows.filter((r) => r.status === "bestaetigt").length;

  return (
    <div className="space-y-4" data-testid="twin-mapping">
      <Card className="flex flex-wrap items-center gap-3 rounded-md border border-primary/30 bg-primary/5 p-4 text-sm">
        <LinkSimple size={18} className="text-primary" />
        <span>Komponententyp ↔ CMS-Core-Komponente. <b>Es wird nie geraten</b> — nur eindeutige (exakte) Treffer werden vorgeschlagen; alles andere bleibt „offen" und wird manuell zugeordnet.</span>
        <div className="ml-auto flex items-center gap-2">
          <Badge variant="outline" className="rounded-md">Bestätigt {bestaetigt}</Badge>
          <Badge variant="outline" className="rounded-md text-primary">Vorschlag {vorschlag}</Badge>
          <Badge variant="outline" className="rounded-md text-warning">Offen {offen}</Badge>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" onClick={autoApply} data-testid="twin-auto-apply" className="gap-1.5"><MagicWand size={15} /> Eindeutige Vorschläge übernehmen</Button>
      </div>

      <div className="space-y-2">
        {data.rows.map((r) => (
          <Card key={r.kind} className="flex flex-wrap items-center gap-3 rounded-md border p-3" data-testid={`twin-map-${r.kind}`}>
            <div className="min-w-[180px]">
              <div className="text-sm font-semibold">{r.name}</div>
              <div className="font-mono text-[10px] text-muted-foreground">{r.kind}</div>
            </div>
            {r.status === "bestaetigt" && <StatusBadge tone="green" testid={`twin-map-status-${r.kind}`}>Bestätigt</StatusBadge>}
            {r.status === "vorschlag" && <StatusBadge tone="blue" testid={`twin-map-status-${r.kind}`}>Vorschlag</StatusBadge>}
            {r.status === "offen" && <StatusBadge tone="amber" testid={`twin-map-status-${r.kind}`}>Offene Zuordnung</StatusBadge>}
            {r.suggestion && r.status !== "bestaetigt" && (
              <span className="flex items-center gap-1 text-xs text-muted-foreground"><Question size={13} /> Vorschlag: {r.suggestion}</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              <Select value={r.cms_komponente_key || "__none__"} onValueChange={(v) => setMap(r.kind, v === "__none__" ? null : v)}>
                <SelectTrigger className="w-64" data-testid={`twin-map-select-${r.kind}`}><SelectValue placeholder="Nicht zugeordnet" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nicht zugeordnet —</SelectItem>
                  {data.core_komponenten.map((k) => <SelectItem key={k.key} value={k.key}>{k.bezeichnung} ({k.typ})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- P2: Mängelregister
function DefectsTab() {
  const [defs, setDefs] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("");
  const load = () => {
    api.get(`/v1/defects${filter ? `?status=${filter}` : ""}`).then((r) => setDefs(r.data)).catch(() => setDefs(false));
    api.get("/v1/defects/summary").then((r) => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filter]);

  const transition = async (id, status) => {
    try {
      await api.post(`/v1/defects/${id}/transition`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const createRepair = async (id) => {
    try {
      const r = await api.post(`/v1/defects/${id}/create-repair`);
      toast.success("Reparaturauftrag erstellt", { description: r.data.title });
      load();
    } catch (e) { toast.error("Fehler", { description: formatApiError(e.response?.data?.detail) }); }
  };

  if (defs === null) return <Skeleton className="h-64 rounded-md" />;
  if (defs === false) return <p className="text-sm text-muted-foreground">Mängelregister konnte nicht geladen werden.</p>;

  return (
    <div className="space-y-4" data-testid="twin-defects">
      {summary && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          {[["Gesamt", summary.total, ""], ["Offen", summary.offen, "text-warning"], ["Beauftragt", summary.beauftragt, "text-blue-500"],
            ["Behoben", summary.behoben, "text-success"], ["Sicherheit offen", summary.offen_sicherheit, "text-danger"]].map(([l, v, cls]) => (
            <Card key={l} className="rounded-md border p-3" data-testid={`twin-defect-kpi-${l}`}>
              <div className={`text-2xl font-black tabular-nums ${cls}`}>{v}</div>
              <div className="mt-0.5"><Label2>{l}</Label2></div>
            </Card>
          ))}
        </div>
      )}
      <div className="flex items-center gap-2">
        <Label2>Filter</Label2>
        <Select value={filter || "__all__"} onValueChange={(v) => setFilter(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-48" data-testid="twin-defect-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Alle</SelectItem>
            {["offen", "beauftragt", "behoben", "abgelehnt"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {defs.length === 0 ? <p className="text-sm text-muted-foreground">Keine Mängel im Register.</p> : (
        <div className="space-y-2">
          {defs.map((d) => (
            <Card key={d.id} className="rounded-md border p-3" data-testid={`twin-defect-${d.id}`}>
              <div className="flex flex-wrap items-center gap-2">
                {d.sicherheitsrelevant && <ShieldWarning size={16} weight="fill" className="text-danger" />}
                <span className="text-sm font-semibold">{d.bezeichnung}</span>
                {d.mangelklasse && <span className={`text-[10px] uppercase ${KLASSE_TONE[d.mangelklasse] || ""}`}>{d.mangelklasse}</span>}
                {d.prioritaet && <span className="text-[10px] uppercase text-muted-foreground">· {d.prioritaet}</span>}
                <StatusBadge tone={DEFECT_STATUS_TONE[d.status]} testid={`twin-defect-status-${d.id}`}>{d.status}</StatusBadge>
                <span className="ml-auto text-xs text-muted-foreground">{d.asset_name} · {d.asset_type}</span>
              </div>
              {d.massnahme && <p className="mt-1 text-xs text-muted-foreground">Maßnahme: {d.massnahme}</p>}
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {(d.cms_reparatur_paket_keys || []).map((k) => <Badge key={k} variant="outline" className="rounded-md gap-1 text-[11px]"><ArrowClockwise size={11} />{k}</Badge>)}
                {d.norm && <Badge variant="outline" className="rounded-md gap-1 text-[11px]"><FileText size={11} />{d.norm}</Badge>}
                <span className="ml-1 text-[10px] text-muted-foreground">{(d.events || []).length} Ereignis(se)</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(NEXT_STATUS[d.status] || []).map((s) => (
                  <Button key={s} size="sm" variant="outline" className="h-7 text-xs" onClick={() => transition(d.id, s)} data-testid={`twin-defect-action-${d.id}-${s}`}>→ {s}</Button>
                ))}
                {d.repair_id ? (
                  <Badge variant="outline" className="rounded-md gap-1 text-[11px] text-blue-600 border-blue-500/40" data-testid={`twin-defect-repair-linked-${d.id}`}>
                    <ArrowClockwise size={11} /> Reparaturauftrag erstellt
                  </Badge>
                ) : d.status !== "abgelehnt" && d.status !== "behoben" && (
                  <Button size="sm" className="h-7 gap-1 text-xs" onClick={() => createRepair(d.id)} data-testid={`twin-defect-create-repair-${d.id}`}>
                    <ArrowClockwise size={12} /> Reparaturauftrag erstellen
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- P1: Wissensverknüpfung
function KnowledgeTab() {
  const [assets, setAssets] = useState(null);
  const [sel, setSel] = useState("");
  const [tk, setTk] = useState(null);

  useEffect(() => { api.get("/assets").then((r) => { setAssets(r.data); if (r.data[0]) setSel(r.data[0].id); }); }, []);
  useEffect(() => { if (sel) api.get(`/v1/assets/${sel}/twin-knowledge`).then((r) => setTk(r.data)); }, [sel]);

  if (assets === null) return <Skeleton className="h-40 rounded-md" />;
  if (!assets.length) return <p className="text-sm text-muted-foreground">Keine Anlagen vorhanden.</p>;

  return (
    <div className="space-y-4" data-testid="twin-knowledge">
      <div className="flex flex-wrap items-center gap-3">
        <Label2>Anlage</Label2>
        <Select value={sel} onValueChange={setSel}>
          <SelectTrigger className="w-80" data-testid="twin-knowledge-select"><SelectValue /></SelectTrigger>
          <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.asset_type})</SelectItem>)}</SelectContent>
        </Select>
        {tk && <Badge variant="outline" className="rounded-md">{tk.total} Komponenten · {tk.offene_zuordnungen} offen</Badge>}
      </div>

      {!tk ? <Skeleton className="h-40 rounded-md" /> : tk.components.map((c) => (
        <Card key={c.component_id} className="rounded-md border p-3" data-testid={`twin-know-${c.component_id}`}>
          <div className="flex flex-wrap items-center gap-2">
            <TreeStructure size={15} className="text-muted-foreground" />
            <span className="text-sm font-semibold">{c.bezeichnung}</span>
            <span className="font-mono text-[10px] text-muted-foreground">{c.kind}</span>
            {c.status === "verknüpft"
              ? <StatusBadge tone="green" testid={`twin-know-status-${c.component_id}`}>Verknüpft: {c.cms_komponente_key}</StatusBadge>
              : <StatusBadge tone="amber" testid={`twin-know-status-${c.component_id}`}>Offene Zuordnung</StatusBadge>}
          </div>
          {c.status === "offen" && <p className="mt-1 text-xs text-muted-foreground">Kein Core-Wissen verfügbar — bitte im Tab „Zuordnung" verknüpfen.</p>}
          {c.core && (
            <div className="mt-1 space-y-1">
              {c.core.wartungsintervall_monate != null && <div className="text-[11px] text-muted-foreground">Wartungsintervall: {c.core.wartungsintervall_monate} Monate{c.core.lebensdauer_zyklen ? ` · Lebensdauer ${c.core.lebensdauer_zyklen.toLocaleString("de-DE")} Zyklen` : ""}</div>}
              <Refs label="Typische Mängel" items={c.core.maengel} icon={WarningCircle} />
              <Refs label="Reparaturpakete" items={c.core.reparatur_pakete} icon={ArrowClockwise} />
              <Refs label="Business Rules" items={c.core.business_rules} icon={GitBranch} />
              <Refs label="Normen" items={c.core.normen} icon={FileText} />
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function Refs({ label, items, icon: Icon }) {
  if (!items?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</span>
      {items.map((x) => <Badge key={x.key} variant="outline" className="rounded-md gap-1 text-[11px]">{Icon && <Icon size={11} />}{x.name}</Badge>)}
    </div>
  );
}

export default function DigitalTwin() {
  return (
    <div className="space-y-6" data-testid="digital-twin-page">
      <PageHeader icon={Gauge} title="Digital Twin" subtitle="Reale Anlage · Wissensverknüpfung zum CMS-Core & Mängelregister (event-sourced)" />
      <Tabs defaultValue="mapping">
        <TabsList>
          <TabsTrigger value="mapping" data-testid="twin-tab-mapping"><LinkSimple size={15} weight="duotone" className="mr-1.5" /> Zuordnung</TabsTrigger>
          <TabsTrigger value="knowledge" data-testid="twin-tab-knowledge"><TreeStructure size={15} weight="duotone" className="mr-1.5" /> Wissensverknüpfung</TabsTrigger>
          <TabsTrigger value="defects" data-testid="twin-tab-defects"><WarningCircle size={15} weight="duotone" className="mr-1.5" /> Mängelregister</TabsTrigger>
        </TabsList>
        <TabsContent value="mapping" className="mt-4"><MappingTab /></TabsContent>
        <TabsContent value="knowledge" className="mt-4"><KnowledgeTab /></TabsContent>
        <TabsContent value="defects" className="mt-4"><DefectsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
